import { Clock, MapPin, Users } from 'lucide-react';
import { Link } from 'react-router';

import { StatusBadge } from '@/components/ui/status-badge';
import { JOB_STATUS_TONES } from '@/hooks/jobs';
import { formatDay, formatDuration, formatWallTime } from '@/lib/dates';
import type { ClientJobListItem } from '@shared/schemas/client-portal';

/**
 * One clean, as a card. **A card at every width, not a table that stacks.**
 *
 * That distinction is the point of FINDINGS.md §10.1: the prototype's client
 * portal uses cards and they reflow cleanly, while the admin tables clip
 * sideways (P6). `DataTable`'s below-768 stacking is the admin fix for the admin
 * problem; the client portal never had that problem and should not inherit its
 * solution.
 *
 * Everything rendered here is a display value the server resolved —
 * `statusLabel`, `serviceTypeName`, crew full names. No slug reaches this file,
 * and there is no second status-to-label mapping in the portal (P5, P17).
 */
export function ClientJobCard({ job }: { job: ClientJobListItem }) {
  return (
    <li>
      <Link
        to={`/portal/cleans/${job.id}`}
        className="block rounded-lg border border-ink-300 bg-white p-4 shadow-card hover:border-honey-500"
      >
        <div className="flex flex-wrap items-start justify-between gap-2">
          <div className="min-w-0">
            <p className="text-h3 text-ink-900">{job.serviceTypeName}</p>
            <p className="mt-0.5 text-meta text-ink-500">
              {job.jobNumber}
              {job.categoryName ? ` · ${job.categoryName}` : ''}
            </p>
          </div>
          <StatusBadge label={job.statusLabel} tone={JOB_STATUS_TONES[job.status]} />
        </div>

        <dl className="mt-3 space-y-1.5 text-body text-ink-700">
          <div className="flex items-start gap-2">
            <dt className="sr-only">When</dt>
            <Clock aria-hidden="true" className="mt-0.5 size-4 shrink-0 text-ink-500" />
            <dd>
              {formatDay(job.scheduledDate)} at {formatWallTime(job.scheduledTime)} ·{' '}
              {formatDuration(job.estimatedDurationMinutes)}
            </dd>
          </div>

          <div className="flex items-start gap-2">
            <dt className="sr-only">Where</dt>
            <MapPin aria-hidden="true" className="mt-0.5 size-4 shrink-0 text-ink-500" />
            <dd className="min-w-0">
              {job.propertyNickname ? (
                <span className="font-medium text-ink-900">{job.propertyNickname} · </span>
              ) : null}
              {job.addressSnapshot}
            </dd>
          </div>

          <div className="flex items-start gap-2">
            <dt className="sr-only">Who</dt>
            <Users aria-hidden="true" className="mt-0.5 size-4 shrink-0 text-ink-500" />
            <dd>
              {job.crew.length > 0 ? (
                // Full names — question 11's default, matching the prototype.
                job.crew.map((member) => member.name).join(', ')
              ) : (
                // A fact and what it means, not a dash (P1).
                <span className="italic text-ink-500">Your cleaner is not assigned yet</span>
              )}
            </dd>
          </div>
        </dl>
      </Link>
    </li>
  );
}
